import React, { Dispatch, Fragment, SetStateAction, useContext, useEffect, useMemo } from "react"
import { Button, buttonVariants } from "@components/common/button"
import { ACCESS_TOKEN, LOCALE, navOptions } from "@pma/utils/constants"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { cn } from "@pma/utils/helpers"
import {
  BellDot,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  FileStack,
  Files,
  InboxIcon,
  Locate,
  Pin,
  Settings,
} from "lucide-react"
import {
  DASHBOARD_PAGE_ROUTE,
  PROJECTS_PAGE_ROUTE,
  SETTINGS_PAGE_ROUTE,
} from "@pma/utils/page-routes"
import MobileNavMenu from "./mobile-nav-menu"
import useTranslator from "@hooks/use-translator"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@components/common/collapsible"
import { DrawerContext, useDrawer } from "@stores/drawer-store"
import GlobalSearch from "../dashboard/global-search"
import { useAuth } from "@pma/stores/auth-store"
import { useQuery } from "@tanstack/react-query"
import axios from "axios"
import { GET_ORGANIZATION, SERVER_URL } from "@pma/utils/api-routes"
import { parseCookies, setCookie } from "nookies"
import { useModal } from "@pma/stores/modal-store"
import { ProjectContext } from "@pma/contexts/ProjectContext"
import { DictType } from "@pma/types"
import { ScrollArea } from "@components/common/scroll-area"
import { useTranslation } from "react-i18next"

interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
}

export function Sidebar({ className, isOpen, setIsOpen }: SidebarProps) {
  const { translate, locale } = useTranslator()
  const { i18n } = useTranslation()
  const navigate = useNavigate()

  const { authConfig, signIn } = useAuth()
  const modal = useModal()
  const { pinnedProjectResult, projectResult } = useContext(ProjectContext)

  const orgId = authConfig?.last_organization_id
  const workspaceDetails = authConfig?.workspace

  const location = useLocation()
  const drawer = useDrawer()

  const accessToken = parseCookies()[ACCESS_TOKEN]
  const userSetLanguage = authConfig?.selected_language

  const pathname = location.pathname
  const activeTab = pathname

  const isHebrew = locale === "he"

  const toggleInbox = (context: DrawerContext) => {
    if (drawer.isOpen && drawer?.context === context) {
      drawer.onClose()
    } else {
      drawer.onOpen()
      drawer.setContext(context)
      return
    }
  }

  const handleClick = () => {
    modal.onOpen()
    modal.setContext("workspace")
  }

  const workSpaceDetails = async () => {
    const response = await axios.get(SERVER_URL + GET_ORGANIZATION + `/${orgId}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    return response?.data
  }

  const { data: organization } = useQuery({
    queryKey: ["workspace-details", orgId],
    queryFn: workSpaceDetails,
    enabled: !workspaceDetails?.owner_id,
  })

  const sidebarLinksMemo = useMemo(() => {
    let pinnedProjectSubItems: DictType[] = []
    const pinnedProjectData = pinnedProjectResult?.data?.items || []
    pinnedProjectData?.forEach((project: DictType) => {
      const temp = {
        label: project?.title,
        href: `${PROJECTS_PAGE_ROUTE}/${project?.id}`,
        icon: project?.address_text ? Locate : Files,
      }

      return pinnedProjectSubItems.unshift(temp)
    })
    const pinnedProjectsLinks = {
      label: "Projects",
      href: PROJECTS_PAGE_ROUTE,
      icon: Files,
      sub: pinnedProjectSubItems,
    }

    const split1 = navOptions.slice(0, 2)
    const split2 = navOptions.slice(2)

    return [...split1, pinnedProjectsLinks, ...split2]
  }, [pinnedProjectResult?.isFetching])

  useEffect(() => {
    if (organization) {
      signIn({ workspace: organization })
      if (userSetLanguage) {
        setCookie(null, LOCALE, userSetLanguage, {
          maxAge: 1000 * 60 * 60 * 24 * 360,
          sameSite: "strict",
          path: "/",
        })
        i18n.changeLanguage(userSetLanguage)
      } else {
        setCookie(null, LOCALE, organization?.owner?.organization_language, {
          maxAge: 1000 * 60 * 60 * 24 * 360,
          sameSite: "strict",
          path: "/",
        })
        i18n.changeLanguage(organization?.owner?.organization_language)
      }
    }
  }, [organization, orgId])

  useEffect(() => {
    if (!pinnedProjectResult?.isFetched) {
      pinnedProjectResult?.refetch()
    }
  }, [pinnedProjectResult])

  return (
    <aside
      className={cn(
        `fixed top-0 z-30 h-[70px] w-full border-r-muted text-white transition-[width]`,
        `lg:bottom-0 lg:right-auto lg:h-svh lg:w-52 ltr:lg:border-r-2`,
        isOpen ? "lg:w-52" : "lg:w-14",
        className,
      )}
      style={{
        background: "linear-gradient(180deg, #343cf9, #7f6aff)",
      }}
    >
      {isOpen && (
        <div className='relative flex items-center justify-between px-2' id='app-logo'>
          <Link
            to={DASHBOARD_PAGE_ROUTE}
            className='flex h-[72px] w-52 flex-1 flex-col items-center justify-center overflow-hidden pt-2 shadow-sm'
          >
            <img
              src={`${process.env.PUBLIC_URL}/group-232.svg`}
              alt='Brand logo'
              height={500}
              width={500}
              className='flex-1 rounded-full object-center'
            />
          </Link>

          <div className='hidden lg:block'>
            <Tooltip>
              <TooltipTrigger
                className={cn("absolute top-3", isHebrew ? "left-14" : "right-14")}
                onClick={handleClick}
              >
                <img
                  src={
                    organization?.owner?.company_logo?.url ||
                    `${process.env.PUBLIC_URL}/group-232.svg`
                  }
                  alt='Brand logo'
                  height={100}
                  width={100}
                  className='h-5 w-5 flex-1 rounded-full border border-white/20 object-center'
                />
              </TooltipTrigger>
              <TooltipContent className='bg-white text-primary'>
                {organization?.owner?.organization_name}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      )}

      {/* Mobile & Tablet Navigation Menu */}
      <div className={cn("flex lg:hidden")}>
        <div className={cn(isHebrew ? "right-14" : "left-24", "absolute top-4")}>
          <GlobalSearch sm />
        </div>

        <div className={cn(isHebrew ? "left-10" : "right-10", "absolute top-4")}>
          <Button
            size={"icon"}
            variant={"ghost"}
            className='hover:bg-slate-200'
            onClick={() => toggleInbox("notifications")}
          >
            <BellDot className='mx-2 h-6 w-6 text-red-400 hover:text-red-300' />
          </Button>
        </div>

        <div className={cn(isHebrew ? "left-3" : "right-3", "absolute top-4")}>
          <Button
            size={"icon"}
            variant={"ghost"}
            className='hover:bg-slate-200'
            onClick={() => toggleInbox("updates")}
          >
            <InboxIcon className='mx-2 h-6 w-6 text-muted hover:text-primary' />
          </Button>
        </div>
      </div>

      <div className={cn(isHebrew ? "right-1" : "left-1", "absolute top-3 flex lg:hidden")}>
        <MobileNavMenu />
      </div>

      {/* Desktop Sidebar - Show only on large screens (1024px+) */}
      <div className='hidden h-full flex-col lg:flex'>
        <div className='h-fit flex-1 border-none px-3 hover:border-primary/20'>
          <div className='mt-6 space-y-4' id='sidebar-navigation'>
            {sidebarLinksMemo?.map((option: DictType, index) => (
              <Fragment key={index}>
                {isOpen ? (
                  <>
                    {option?.sub && option.sub.length > 0 ? (
                      <Collapsible defaultOpen={activeTab.includes(option?.href)}>
                        <CollapsibleTrigger
                          className={cn(
                            buttonVariants({ variant: "ghost", size: "sm" }),
                            "group m-0 w-full justify-start py-[18px] hover:bg-pms_black hover:text-muted-foreground",
                            activeTab.includes(option?.href) && "bg-pms_black text-white",
                          )}
                          onClick={() => {
                            if (option?.label.toLocaleLowerCase().includes("project")) {
                              navigate(option?.href)
                            }
                          }}
                        >
                          <option.icon className='h-4 w-4' />
                          <span className='mx-2'>{translate(option.label)}</span>

                          <span
                            className={cn(
                              'ml-auto transition-all group-data-[state="open"]:-rotate-180',
                            )}
                          >
                            <ChevronDown />
                          </span>
                        </CollapsibleTrigger>
                        <CollapsibleContent className='collapsibleDropdown' asChild>
                          <ul className='relative'>
                            <Pin
                              size={16}
                              className={cn(
                                "absolute -top-2",
                                isHebrew ? "-right-1 rotate-45" : "left-1 -rotate-45",
                                !option?.label.includes("project") && "hidden",
                              )}
                            />
                            <ScrollArea
                              className={cn(
                                "ml-4 mt-2 overflow-x-hidden rounded-md bg-card/20 px-1",
                                option.sub.length > 4 && "h-[200px]",
                              )}
                            >
                              {option?.sub!.map((sublink: DictType, index: number) => {
                                return (
                                  <Link
                                    to={sublink.href}
                                    key={sublink.label + index}
                                    className={cn("flex w-full items-center justify-start")}
                                  >
                                    <li className='my-1 w-full'>
                                      <Tooltip>
                                        <TooltipTrigger className='w-full'>
                                          <Button
                                            key={sublink?.label}
                                            variant={"ghost"}
                                            className={cn(
                                              "w-full h-auto items-start justify-start py-1.5 hover:bg-pms_black hover:text-muted-foreground",
                                              `${activeTab}` === sublink?.href &&
                                              "rounded-md bg-pms_black/50 text-white",
                                              isHebrew ? "text-right" : "text-left",
                                            )}
                                          >
                                            <sublink.icon className='h-4 w-4 mt-0.5 shrink-0' />
                                            <span
                                              className={cn(
                                                "mx-1 flex-1 min-w-0 whitespace-normal break-words text-start text-sm leading-[1rem]",
                                              )}
                                            >
                                              {translate(sublink.label)}
                                            </span>
                                          </Button>
                                        </TooltipTrigger>
                                        {option?.label.includes("project") && (
                                          <TooltipContent>
                                            <span className='mx-1'>
                                              {translate("Go to project")}{" "}
                                              {translate(sublink.label)}
                                            </span>
                                          </TooltipContent>
                                        )}
                                      </Tooltip>
                                    </li>
                                  </Link>
                                )
                              })}
                            </ScrollArea>

                            {/* {option?.label.toLocaleLowerCase().includes("project") && (
                              <Link
                                to={PROJECTS_PAGE_ROUTE}
                                className={cn("flex w-full items-center justify-start")}
                              >
                                <li className='my-1 ml-4'>
                                  <Button
                                    variant={"ghost"}
                                    className={cn(
                                      "w-full justify-start hover:bg-pms_black hover:text-muted-foreground",
                                      `${activeTab}` === PROJECTS_PAGE_ROUTE &&
                                        "rounded-md bg-pms_black/50 text-white",
                                    )}
                                  >
                                    <FileStack className='h-4 w-4' />
                                    <span className='mx-1 w-[12ch] truncate text-ellipsis'>
                                      {translate("All Projects")}
                                    </span>
                                  </Button>
                                </li>
                              </Link>
                            )} */}
                          </ul>
                        </CollapsibleContent>
                      </Collapsible>
                    ) : (
                      <Link
                        to={option.href}
                        key={index}
                        className={cn(
                          "flex items-center justify-start",
                          `${activeTab}` === option?.href && "rounded-md bg-pms_black text-white",
                        )}
                      >
                        <Button
                          variant={"ghost"}
                          className='w-full justify-start hover:bg-pms_black hover:text-muted-foreground'
                        >
                          <option.icon className='h-4 w-4' />
                          <span className='mx-2'>{translate(option.label)}</span>
                        </Button>
                      </Link>
                    )}
                  </>
                ) : (
                  <Link
                    to={option.href}
                    key={index}
                    className={cn(
                      "flex items-center justify-start",
                      `${activeTab}` === option?.href && "rounded-md bg-pms_black text-white",
                    )}
                  >
                    <Button
                      size={"icon"}
                      className={cn(
                        "w-full justify-center hover:bg-pms_black hover:text-muted-foreground",
                        `${activeTab}` === option?.href && "rounded-md bg-pms_black text-white",
                      )}
                    >
                      <option.icon className='h-4 w-4 text-white' />
                    </Button>
                  </Link>
                )}
              </Fragment>
            ))}
          </div>
        </div>
        <Link
          to={SETTINGS_PAGE_ROUTE}
          className={cn(
            "absolute bottom-20 mx-3 flex items-center justify-start",
            `${activeTab}` === SETTINGS_PAGE_ROUTE && "rounded-md bg-pms_black text-white",
          )}
        >
          {isOpen ? (
            <Button
              variant={"ghost"}
              id='account-settings-button'
              className='justify-start hover:bg-pms_black hover:text-muted-foreground lg:w-44'
            >
              <Settings className='h-4 w-4' />
              <span className='mx-2'>{translate("Settings")}</span>
            </Button>
          ) : (
            <Button
              size={"icon"}
              className={cn(
                "w-10 justify-center hover:bg-pms_black hover:text-muted-foreground",
                `${activeTab}` === SETTINGS_PAGE_ROUTE && "rounded-md bg-pms_black text-white",
              )}
            >
              <Settings className='h-4 w-4 text-white' />
            </Button>
          )}
        </Link>

        <div
          onClick={() => setIsOpen((prev) => !prev)}
          className={cn(
            isHebrew ? "-left-5" : "-right-5",
            "absolute bottom-6 hidden h-9 w-9 items-center justify-center whitespace-nowrap rounded-full border border-input bg-background text-sm font-medium shadow-sm transition-colors hover:scale-105 hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 lg:inline-flex",
          )}
        >
          <Tooltip>
            <TooltipTrigger>
              {isHebrew ? (
                <ChevronsRight className={`h-5 w-5 text-black ${isOpen ? "" : "rotate-180"}`} />
              ) : (
                <ChevronsLeft className={`h-5 w-5 text-black ${isOpen ? "" : "rotate-180"}`} />
              )}
            </TooltipTrigger>
            <TooltipContent side={isHebrew ? "left" : "right"} align='center' sideOffset={8}>
              {isOpen ? translate("Collapse") : translate("Expand")}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </aside>
  )
}
